import { ModelTypes, topliApi, Webinar } from '@/shared/api/topli/TopliApi'
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { formatDateForSending } from '@/shared/lib/utils'
import { editTranslation } from '@/entities/ContentTranslation'
import { errorHandler } from '@/shared/lib/errorHandler'
import { EditWebinarFormState } from '../model/types'
import { currentWebinarByIdQueryKey, webinarListsByPageQueryKey } from '@/entities/Webinars'
import { UploadWebinarPhoto } from '@/entities/Photos/api/photo-webinar'

export const editWebinar = async (state: EditWebinarFormState) => {
  const { id, translations, release_date, thumbnail, shouldUpdateTranslations, ...restState } =
    state
  const body = {
    release_date: formatDateForSending(release_date) as string,
    name: translations.tr.name,
    description: translations?.tr.description,
    ...(typeof thumbnail === 'string' && thumbnail ? { thumbnail } : {}),
    ...restState,
  }
  const response = await topliApi.webinars.update(id, body)
  return response
}

export const useEditWebinarApi = (): UseMutationResult<Webinar, Error, EditWebinarFormState> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: state => editWebinar(state),
    onSuccess: async (res, state) => {
      try {
        if (state.shouldUpdateTranslations) {
          await editTranslation(
            { translations: state.translations, modelType: ModelTypes.Webinar },
            res.id,
          )
        }

        if (state.thumbnail && typeof state.thumbnail === 'object') {
          await UploadWebinarPhoto({
            file: state.thumbnail,
            id: res.id,
          })
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [webinarListsByPageQueryKey],
            refetchType: 'active',
          }),
          queryClient.invalidateQueries({
            queryKey: [currentWebinarByIdQueryKey],
            refetchType: 'active',
          }),
        ])
      } catch (error) {
        errorHandler(error)
      }
    },
  })
}
